// @flow
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import { modal } from '../../../modals/BasicModal';
import CCIcon from '../../../components/CCIcon';
import { FaExclamationTriangle } from 'react-icons/lib/fa';
import * as subscriptionApi from '../../../api/billing/subscription';
import {isAdmin} from '../../../utils/authentication'
import type { Price } from "../../../api/billing/types";
import LoadingOverlay from "../../../components/LoadingOverlay";
import AvailableProducts from "./AvailableProducts";
import CurrentPlan from "./CurrentPlan";
import * as JWTService from '../../../utils/JWTService';
import {Panel, Row, Col, Button} from "react-bootstrap";

function Billing(props: {
    email: string,
    subscription: any,
    organisation: any,
}): React.Element<any> {
    const [updating, setUpdating] = useState(false);
    const [info, setInfo] = useState();
    const [usage, setUsage] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [checkoutIsLoading, setCheckoutIsLoading] = useState(false);
    const subscription = props.subscription;


    const handleManageBilling = async () => {
        setIsLoading(true);
        try {
            const response = await fetch('/api/v1/billing/subscription/manage', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${JWTService.getAuthToken()}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to create billing portal session');
            }

            const data = await response.json();
            if (data.url) {
                window.location.href = data.url;
            }
        } catch (error) {
            console.error('Error managing billing:', error);
            modal('Error',
                <div style={{textAlign: 'center', height: 250}}>
                    <div style={{fontSize: 100, color: '#ff6b6b'}}>
                        <FaExclamationTriangle/>
                    </div>
                    <b>Sorry, we were unable to access the billing portal. Please try again later.</b>
                </div>
            ).catch(console.warn);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangeSubscriptionSession = async () => {
        setIsLoading(true);
        try {
            const response = await fetch('/api/v1/billing/subscription/change', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${JWTService.getAuthToken()}`
                },
                body: JSON.stringify({
                    subscriptionId: subscription.id
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error('We could not update this subscription: ' + errorData.detail || '');
            }

            const data = await response.json();
            if (data.url) {
                window.location.href = data.url;
            }
        } catch (error) {
            modal('Error',
                <div style={{textAlign: 'center', height: 250}}>
                    <div style={{fontSize: 100, color: '#ff6b6b'}}>
                        <FaExclamationTriangle/>
                    </div>
                    <div>
                        <b>{error.message}</b>
                    </div>
                    <br/>
                    <div>
                        <Button href="mailto:contact@featureflow.io"
                                bsStyle="primary" bsSize="sm">
                            Contact us
                        </Button>
                    </div>
                </div>
            ).catch(console.warn);
        } finally {
            setIsLoading(false);
        }
    }


    useEffect(() => {
        Promise.all([
            subscriptionApi.getBillingInfo(),
            subscriptionApi.getBillingUsage(),
        ]).then(([info, usage]) => {

            setTimeout(() => {
                setInfo(info);
                setUsage(usage);
            }, 1);
        }).catch(console.warn);
    }, []);

    if (!isAdmin()) {
        return (
            <div>
                <h2>Billing</h2>
                <div style={{textAlign: 'center', marginTop: 100, marginBottom: 100}}>
                    <p>Only organisation admins may manage billing. Please contact your administrator to update billing
                        details. </p>
                    <p>For details on pricing plans, please see <a href={"https://www.featureflow.io/pricing/"}
                                                                   target={"_blank"}>here</a></p>
                </div>
            </div>
        )
    }

    if (checkoutIsLoading) {
        return <LoadingOverlay visible={checkoutIsLoading}/>
    }
    if (info && usage && !updating) {
        return (
            <>
                <Row>
                    <Col xs={12}>
                        <h2>Billing</h2>

                        <div className="billing-info">
                            <Panel>
                                <Panel.Heading style={{fontSize: 18}}>Billing Information</Panel.Heading>
                                <Panel.Body>
                                    {info.lastFour ? (
                                        <div>
                                            <p>
                                                This account is billed to:&nbsp;<CCIcon brand={info.brand}/>&nbsp; 
                                                <b>{info.brand} ending in {info.lastFour}</b>
                                            </p>
                                            <p>Billing email: {info?.customer?.email || 'Not set'}</p>
                                            <p>Name: {info?.customer?.name || 'Not set'}</p>
                                            <p>Phone: {info?.customer?.phone || 'Not set'}</p>
                                        </div>
                                    ) : (
                                        <div>
                                            <p>This account has no billing information</p>
                                        </div>
                                    )}
                                    <div style={{ margin: '20px 0' }}>
                                            <Button 
                                                onClick={handleManageBilling}
                                                disabled={isLoading}
                                                bsStyle="primary">
                                                    Manage Billing Details
                                            </Button>
                                            <Button 
                                                onClick={handleChangeSubscriptionSession}
                                                disabled={checkoutIsLoading}
                                                bsStyle="primary"
                                                style={{ marginLeft: '10px', padding: '10px 20px' }}>
                                                    Update Subscription
                                            </Button>
                                        </div>
                                </Panel.Body>
                            </Panel>
                        </div>
                       
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                    <h2>Plans</h2>
                    <AvailableProducts/>        
                    </Col>
                </Row>
                
                <Row id="update-subscription-row">
                    <Col xs={12}>

                    </Col>
                </Row>
                <Row>
                    <CurrentPlan subscription={subscription} usage={usage}/>
                </Row>
            </>
        )
    } else {
        return (
            <div>
                <h2>Billing</h2>
                <div style={{textAlign: 'center', marginTop: 100, marginBottom: 100}}>
                    <p>{updating ? 'Updating billing information...' : 'Retrieving billing information...'}</p>
                </div>
            </div>
        )
    }
    
    
    
}

function mapStateToProps(state) {
    return {
        email: state.principalUserOutline.email,
        subscription: state.subscriptionOutline,
        organisation: state.organisation
    }
}

export default connect(mapStateToProps)(Billing);